// admin-email-log.js - outbound-email audit surface for admins.
// Reads /api/log/emails (LogController + EmailLogService). Shows every email the
// app sent (and any that failed), with recipient / category / status / window
// filters. Mirrors admin-activity.js.

import { getUser } from './auth.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

async function api(method, path) {
  const headers = { 'Content-Type': 'application/json' };
  if (token()) headers['Authorization'] = 'Bearer ' + token();
  const res = await fetch(path, { method, headers });
  if (!res.ok) throw new Error(`${method} ${path}: ${res.status}`);
  return res.json();
}

function escapeHtml(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;' }[c]));
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return escapeHtml(iso);
  // Compact UTC: YYYY-MM-DD HH:MM
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

const CAT_COLOR = {
  trial: '#6366f1', beta: '#8b5cf6', license: '#10b981', expiry: '#f59e0b',
  reset: '#0ea5e9', support: '#ec4899', account: '#ef4444', welcome: '#22c55e', other: '#64748b',
};

function catChip(cat) {
  const c = CAT_COLOR[cat] || CAT_COLOR.other;
  return `<span style="display:inline-block;padding:1px 7px;border-radius:10px;font-size:10px;` +
    `border:1px solid ${c};color:${c};">${escapeHtml(cat || 'other')}</span>`;
}

async function refresh() {
  // /api/log/emails is admin-only; bail unless we have an admin user (also covers
  // the pre-login boot window when getUser() is still null).
  if (getUser()?.role !== 'admin') return;
  const status = document.getElementById('admin-eml-status');
  const body = document.getElementById('admin-eml-body');
  if (!status || !body) return;
  const days = parseInt(document.getElementById('admin-eml-days').value) || 14;
  const to = document.getElementById('admin-eml-to').value.trim();
  const category = document.getElementById('admin-eml-category').value;
  const st = document.getElementById('admin-eml-statusfilter').value;
  const max = parseInt(document.getElementById('admin-eml-max').value) || 500;
  status.innerHTML = '<i class="fas fa-spinner fa-spin"></i> loading...';
  body.innerHTML = '<tr><td colspan="4" class="admin-empty">Loading...</td></tr>';
  try {
    const params = new URLSearchParams({ days, max });
    if (to) params.set('to', to);
    if (category && category !== 'all') params.set('category', category);
    if (st) params.set('status', st);
    const res = await api('GET', '/api/log/emails?' + params);
    const data = res.data || {};
    const rows = data.rows || [];
    const failed = rows.filter(r => !r.success).length;
    status.innerHTML =
      `<i class="fas fa-check-circle" style="color:var(--accent,#10b981);"></i> ` +
      `${rows.length} email${rows.length === 1 ? '' : 's'} over last ${data.days}d` +
      (failed ? ` <span style="color:#ef4444;">(${failed} failed)</span>` : '');
    if (rows.length === 0) {
      body.innerHTML = '<tr><td colspan="4" class="admin-empty">No matching emails.</td></tr>';
      return;
    }
    body.innerHTML = rows.map(r => {
      const ok = r.success;
      const statusCell = ok
        ? '<span style="color:#10b981;"><i class="fas fa-check"></i> sent</span>'
        : `<span style="color:#ef4444;" title="${escapeHtml(r.error || '')}"><i class="fas fa-triangle-exclamation"></i> failed</span>`;
      return `<tr>
        <td style="white-space:nowrap;">${fmtTime(r.sentAt)}</td>
        <td>${escapeHtml(r.to)}</td>
        <td>${catChip(r.category)} ${escapeHtml(r.subject)}</td>
        <td style="white-space:nowrap;">${statusCell} <span style="color:var(--text-dim);font-size:10px;">${escapeHtml(r.transport)}</span></td>
      </tr>`;
    }).join('');
  } catch (e) {
    status.innerHTML = `<span style="color:#ef4444;"><i class="fas fa-times-circle"></i> ${escapeHtml(e.message)}</span>`;
    body.innerHTML = '<tr><td colspan="4" class="admin-empty">Could not load the email log.</td></tr>';
  }
}

export function initAdminEmailLog() {
  const btn = document.getElementById('btn-eml-refresh');
  if (btn) btn.addEventListener('click', refresh);
  ['admin-eml-to', 'admin-eml-category', 'admin-eml-statusfilter', 'admin-eml-days'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('keyup', (e) => { if (e.key === 'Enter') refresh(); });
    if (el && el.tagName === 'SELECT') el.addEventListener('change', refresh);
  });
  // Load when the Emails subtab is opened (the panel is hidden at boot).
  const tab = document.querySelector('.atab[data-atab="emails"]');
  if (tab) tab.addEventListener('click', () => setTimeout(refresh, 50), { once: false });
}
